import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  HiOutlinePlay,
  HiOutlineSave,
  HiOutlineUsers,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
} from 'react-icons/hi';
import { settingsApi } from '../../services/api';
import { formatCalendarDateDMY } from '../../utils/timezoneDisplay';

function formatTime12h(time24) {
  if (!time24) return '—';
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr || '00';
  if (Number.isNaN(h)) return time24;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

function formatRunAt(runAt) {
  if (!runAt) return '—';
  const [datePart, timePart] = runAt.split(' ');
  if (!datePart || !timePart) return runAt;
  return `${formatCalendarDateDMY(datePart)} at ${formatTime12h(timePart)}`;
}

const PatientAttendanceSettings = () => {
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [time, setTime] = useState('23:00');
  const [lastRunDate, setLastRunDate] = useState(null);
  const [logDays, setLogDays] = useState(30);
  const [runs, setRuns] = useState([]);
  const [logSummary, setLogSummary] = useState({ total: 0, total_runs: 0 });
  const [expandedRuns, setExpandedRuns] = useState({});

  const loadLogs = useCallback(async (days = 30) => {
    setLogsLoading(true);
    try {
      const res = await settingsApi.getTherapistPatientAttendanceAbsentLogs({ days });
      const data = res.data.data || {};
      setRuns(data.runs || []);
      setLogSummary({
        total: data.total_therapists || 0,
        total_runs: data.total_runs || 0,
      });
      const nextExpanded = {};
      (data.runs || []).slice(0, 1).forEach((run) => {
        nextExpanded[`${run.run_at}|${run.attendance_date}`] = true;
      });
      setExpandedRuns(nextExpanded);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load auto-absent logs');
      setRuns([]);
      setLogSummary({ total: 0, total_runs: 0 });
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settingsApi.getTherapistPatientAttendanceAbsent();
      const data = res.data.data || {};
      setEnabled(data.enabled !== false);
      setTime(data.time || '23:00');
      setLastRunDate(data.last_run_date || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadLogs(30);
  }, [load, loadLogs]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsApi.updateTherapistPatientAttendanceAbsent({ enabled, time });
      toast.success('Settings saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (
      !window.confirm(
        'Mark therapists absent when any assigned patient is missing attendance for today?\n\nThis overwrites admin present/leave marks when the rule applies.'
      )
    ) {
      return;
    }
    setRunning(true);
    try {
      const res = await settingsApi.runTherapistPatientAttendanceAbsentNow();
      toast.success(res.data.message || 'Done');
      await load();
      await loadLogs(logDays);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] w-full items-center justify-center">
        <div className="h-9 w-9 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1400px] mx-auto space-y-8 pb-8 animate-fade-in">
      <div>
        <h1 className="font-display text-2xl font-bold text-gray-900">Attendance automation</h1>
        <p className="mt-1 max-w-3xl text-sm text-gray-500">
          Therapist staff attendance is derived from patient attendance marks on their assigned
          patients.
        </p>
      </div>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            Therapist auto-absent (from patient attendance)
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            Example: a therapist assigned to 4 patients is marked <strong>absent</strong> if{' '}
            <strong>any</strong> assigned patient is missing attendance for the day. Only when{' '}
            <strong>all</strong> assigned patients have attendance marked is the therapist not marked
            absent. <strong>Leave</strong> and <strong>Half day</strong> marks are preserved; only{' '}
            <strong>Present</strong> (or no mark) can be overwritten to <strong>Absent</strong> when
            the rule applies.
          </p>
          <p className="mt-2 text-xs text-gray-500">
            History shows when the job actually ran (IST). <strong>Run now</strong> runs immediately;
            the daily schedule only fires at the saved time below. The job does not run on Sundays.
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Schedule:{' '}
            <span className="font-semibold text-gray-800">
              {enabled ? formatTime12h(time) : 'Off'}
            </span>
            {lastRunDate ? (
              <span className="text-gray-400"> · Last run {lastRunDate}</span>
            ) : null}
          </p>
        </div>

        <form
          onSubmit={handleSave}
          className="card flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-900">Enabled</span>
            </label>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 whitespace-nowrap">Daily time (IST)</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                disabled={!enabled}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                required
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={saving} className="btn btn-primary text-sm">
              <HiOutlineSave className="mr-1.5 inline h-4 w-4" />
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleRunNow}
              disabled={running}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              <HiOutlinePlay className="mr-1.5 inline h-4 w-4" />
              {running ? 'Running…' : 'Run now'}
            </button>
          </div>
        </form>
      </section>


      <div className="card p-0 overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Auto-absent history</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Each row is a day the job ran. <strong>0 therapists</strong> means the job completed
              but nobody was marked absent.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm">
              <HiOutlineUsers className="h-4 w-4 text-gray-400" />
              <span>
                <span className="font-semibold text-gray-900">{logSummary.total_runs}</span>
                <span className="text-gray-500"> runs</span>
              </span>
              <span className="text-gray-300">|</span>
              <span>
                <span className="font-semibold text-gray-900">{logSummary.total}</span>
                <span className="text-gray-500"> marked absent</span>
              </span>
            </div>
            <select
              value={logDays}
              onChange={(e) => {
                const d = parseInt(e.target.value, 10);
                setLogDays(d);
                loadLogs(d);
              }}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
              aria-label="History period"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        {logsLoading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
          </div>
        ) : runs.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500">
            No therapist auto-absent runs in this period.
          </div>
        ) : (
          <div className="max-h-[min(60vh,560px)] overflow-y-auto overflow-x-auto divide-y divide-gray-100">
            {runs.map((run) => {
              const key = `${run.run_at}|${run.attendance_date}`;
              const open = expandedRuns[key];
              const items = run.therapists || [];
              const itemCount = run.therapist_count ?? items.length;
              return (
                <div key={key}>
                  <button
                    type="button"
                    onClick={() => setExpandedRuns((prev) => ({ ...prev, [key]: !prev[key] }))}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 sm:px-6"
                  >
                    {open ? (
                      <HiOutlineChevronDown className="h-5 w-5 shrink-0 text-gray-400" />
                    ) : (
                      <HiOutlineChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Attendance date: {formatCalendarDateDMY(run.attendance_date)}
                      </p>
                      <p className="text-xs text-gray-500">System ran {formatRunAt(run.run_at)}</p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                        itemCount > 0
                          ? 'bg-violet-100 text-violet-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {itemCount} therapist{itemCount !== 1 ? 's' : ''}
                    </span>
                  </button>
                  {open ? (
                    <div className="border-t border-gray-100 bg-gray-50/80 px-4 pb-4 pt-2 sm:px-6 sm:pl-14">
                      {itemCount === 0 ? (
                        <p className="py-3 text-sm text-gray-600">
                          No therapists marked absent — all assigned patient attendance was complete
                          (or no eligible therapists).
                        </p>
                      ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-500">
                            <th className="pb-2 font-medium">#</th>
                            <th className="pb-2 font-medium">Therapist</th>
                            <th className="pb-2 font-medium">Email</th>
                            <th className="pb-2 font-medium">Patients</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200/80">
                          {items.map((item, idx) => (
                            <tr key={`${item.employee_email}-${idx}`} className="text-gray-800">
                              <td className="py-2 pr-4 text-gray-400 w-8">{idx + 1}</td>
                              <td className="py-2 font-medium">{item.employee_name}</td>
                              <td className="py-2 text-gray-600">{item.employee_email || '—'}</td>
                              <td className="py-2 text-gray-600">
                                {item.assigned_count != null
                                  ? `${item.marked_patient_count ?? '?'}/${item.assigned_count} marked`
                                  : '—'}
                                {item.unmarked_patient_count > 0 ? (
                                  <span className="ml-1 text-rose-600">
                                    ({item.unmarked_patient_count} missing)
                                  </span>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientAttendanceSettings;
